import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Palette,
  Sun,
  Moon,
  Monitor,
  Save,
  RotateCcw,
  Loader2,
  Trash2,
  Image,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { isSuperAdmin } from "@/lib/roles";
import { ReadOnlyBadge } from "@/components/portal/ReadOnly";
import { Textarea } from "@/components/ui/textarea";
import {
  themeApi,
  ThemeConfig,
  ThemeColorMode,
  defaultLightColors,
  defaultDarkColors,
} from "@/services/api/theme";
import {
  invalidColorKeys,
  isValidHsl,
  normaliseHsl,
  normaliseThemeColors,
  withDefaultColors,
} from "@/lib/themeHsl";
import {
  brandApi,
  type OrgBrandProfile,
} from "@/services/api/brand";

export const Route = createFileRoute("/portal/platform/theme")({
  component: ThemeEditorPage,
});

const COLOR_PRESETS = [
  { name: "Indigo", primary: "243 75% 59%", accent: "259 84% 67%" },
  { name: "Blue", primary: "217 91% 60%", accent: "199 89% 48%" },
  { name: "Green", primary: "142 71% 45%", accent: "160 84% 39%" },
  { name: "Orange", primary: "25 95% 53%", accent: "31 97% 72%" },
  { name: "Red", primary: "0 84% 60%", accent: "0 91% 71%" },
  { name: "Purple", primary: "270 91% 65%", accent: "280 81% 76%" },
];

const FONT_OPTIONS = [
  { value: "inter", label: "Inter" },
  { value: "roboto", label: "Roboto" },
  { value: "opensans", label: "Open Sans" },
  { value: "system", label: "System Default" },
];

// ---------------------------------------------------------------------------
// Reusable ColorInput component
// ---------------------------------------------------------------------------

interface ColorInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function ColorInput({ label, value, onChange, disabled }: ColorInputProps) {
  const invalid = !isValidHsl(value);
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div
          className="w-10 h-9 rounded border"
          style={{ backgroundColor: `hsl(${value})` }}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // Tidy picker spellings ("9° 82% 57%", "hsl(9, 82%, 57%)") on blur
          onBlur={() => {
            const normalised = normaliseHsl(value);
            if (normalised !== value) onChange(normalised);
          }}
          placeholder="0 0% 100%"
          className={`flex-1 font-mono text-sm${invalid ? " border-destructive" : ""}`}
          aria-invalid={invalid}
          disabled={disabled}
        />
      </div>
      {invalid && !disabled && (
        <p className="text-xs text-destructive">
          Use H S% L%, e.g. 9 82% 57%
        </p>
      )}
    </div>
  );
}

const COLOR_LABELS: Partial<Record<keyof ThemeColorMode, string>> = {
  primary: "Primary",
  "primary-foreground": "Primary Foreground",
  background: "Background",
  foreground: "Foreground",
  accent: "Accent",
  muted: "Muted",
  success: "Success",
  warning: "Warning",
};

/**
 * Normalise both modes and check them against the backend's HSL format.
 * Returns null (after a toast naming the bad fields) when a save would 422.
 */
function prepareColorsForSave(
  light: ThemeColorMode,
  dark: ThemeColorMode,
): { light: ThemeColorMode; dark: ThemeColorMode } | null {
  const normalised = {
    light: normaliseThemeColors(light),
    dark: normaliseThemeColors(dark),
  };
  const bad = (["light", "dark"] as const).flatMap((mode) =>
    invalidColorKeys(normalised[mode]).map(
      (key) => `${COLOR_LABELS[key] ?? key} (${mode})`,
    ),
  );
  if (bad.length > 0) {
    toast.error(
      `Invalid colour format: ${bad.join(", ")}. Use H S% L%, e.g. 9 82% 57%.`,
    );
    return null;
  }
  return normalised;
}

// ---------------------------------------------------------------------------
// ThemeEditorPanel -- renders the full theme editor UI for either org or
// platform scope. Receives all handlers and state via props so it can be
// rendered independently for each scope.
// ---------------------------------------------------------------------------

interface ThemeEditorPanelProps {
  // State
  loading: boolean;
  saving: boolean;
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  themeMode: "light" | "dark" | "system";
  setThemeMode: (v: "light" | "dark" | "system") => void;
  lightColors: ThemeColorMode;
  darkColors: ThemeColorMode;
  radius: string;
  portalName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  uploadingFavicon: boolean;
  hasCustomTheme: boolean;
  readOnly: boolean;

  // Handlers
  onUpdateLightColor: (key: keyof ThemeColorMode, value: string) => void;
  onUpdateDarkColor: (key: keyof ThemeColorMode, value: string) => void;
  onSetRadius: (value: string) => void;
  onSetPortalName: (value: string) => void;
  onApplyPreset: (preset: (typeof COLOR_PRESETS)[0]) => void;
  onSave: () => void;
  onReset: () => void;
  onCancel: () => void;
  onFaviconUpload: (file: File) => void;
  onFaviconDelete: () => void;
  logoVariantUrls?: { light_bg?: string; dark_bg?: string };
  uploadingLogoVariant?: string | null;
  onLogoVariantUpload?: (variant: "light_bg" | "dark_bg", file: File) => void;
  onLogoVariantDelete?: (variant: "light_bg" | "dark_bg") => void;

  // Labels
  scopeLabel: string; // e.g. "Organisation" or "Platform"
}

function ThemeEditorPanel({
  loading,
  saving,
  isEditing,
  setIsEditing,
  themeMode,
  setThemeMode,
  lightColors,
  darkColors,
  radius,
  portalName,
  logoUrl,
  faviconUrl,
  uploadingFavicon,
  hasCustomTheme,
  readOnly,
  onUpdateLightColor,
  onUpdateDarkColor,
  onSetRadius,
  onSetPortalName,
  onApplyPreset,
  onSave,
  onReset,
  onCancel,
  onFaviconUpload,
  onFaviconDelete,
  logoVariantUrls,
  uploadingLogoVariant,
  onLogoVariantUpload,
  onLogoVariantDelete,
  scopeLabel,
}: ThemeEditorPanelProps) {
  const [activeTab, setActiveTab] = useState("colors");
  const [editingColorMode, setEditingColorMode] = useState<"light" | "dark">(
    "light",
  );

  const disabled = !isEditing || readOnly;
  const currentColors = editingColorMode === "light" ? lightColors : darkColors;
  const updateColor = (key: keyof ThemeColorMode, value: string) => {
    if (editingColorMode === "light") {
      onUpdateLightColor(key, value);
    } else {
      onUpdateDarkColor(key, value);
    }
  };

  const parseRadius = (r: string): number => {
    const match = r.match(/^([\d.]+)rem$/);
    return match ? parseFloat(match[1]) * 16 : 8;
  };
  const formatRadius = (px: number): string => `${px / 16}rem`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info banner when using platform defaults (org scope only) */}
      {!hasCustomTheme && scopeLabel === "Organisation" && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <CardContent className="flex items-center gap-3 py-4">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              This organisation is currently using the platform default theme.
              Save changes to create a custom theme.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      {!readOnly && (
        <div className="flex items-center justify-end gap-2">
          {isEditing ? (
            <>
              {hasCustomTheme && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReset}
                  disabled={saving}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reset to Defaults
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={onSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Theme
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setIsEditing(true)}>
              Edit Theme
            </Button>
          )}
        </div>
      )}

      {/* Theme Mode Selection */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Appearance Mode</CardTitle>
          <CardDescription className="text-xs">
            {scopeLabel === "Platform"
              ? "Default color scheme for the platform"
              : "Choose your organisation's preferred color scheme"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <button
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                themeMode === "light"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/50"
              }`}
              onClick={() => setThemeMode("light")}
              disabled={disabled}
            >
              <Sun className="h-6 w-6" />
              <span className="text-sm font-medium">Light</span>
            </button>
            <button
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                themeMode === "dark"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/50"
              }`}
              onClick={() => setThemeMode("dark")}
              disabled={disabled}
            >
              <Moon className="h-6 w-6" />
              <span className="text-sm font-medium">Dark</span>
            </button>
            <button
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors ${
                themeMode === "system"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground/50"
              }`}
              onClick={() => setThemeMode("system")}
              disabled={disabled}
            >
              <Monitor className="h-6 w-6" />
              <span className="text-sm font-medium">System</span>
            </button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="typography">Typography</TabsTrigger>
          <TabsTrigger value="layout">Layout</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
        </TabsList>

        <TabsContent value="colors" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Color Presets</CardTitle>
              <CardDescription className="text-xs">
                Quick select from predefined color schemes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-6 gap-3">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                      lightColors.primary === preset.primary
                        ? "border-primary"
                        : "border-border hover:border-muted-foreground/50"
                    }`}
                    onClick={() => onApplyPreset(preset)}
                    disabled={disabled}
                  >
                    <div className="flex gap-1">
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{
                          backgroundColor: `hsl(${preset.primary})`,
                        }}
                      />
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{
                          backgroundColor: `hsl(${preset.accent})`,
                        }}
                      />
                    </div>
                    <span className="text-xs">{preset.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">
                    Custom Colors (HSL)
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Fine-tune individual color values
                  </CardDescription>
                </div>
                <Select
                  value={editingColorMode}
                  onValueChange={(v) =>
                    setEditingColorMode(v as "light" | "dark")
                  }
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light Mode</SelectItem>
                    <SelectItem value="dark">Dark Mode</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <ColorInput
                  label="Primary"
                  value={currentColors.primary}
                  onChange={(v) => updateColor("primary", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Primary Foreground"
                  value={currentColors["primary-foreground"]}
                  onChange={(v) => updateColor("primary-foreground", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Background"
                  value={currentColors.background}
                  onChange={(v) => updateColor("background", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Foreground"
                  value={currentColors.foreground}
                  onChange={(v) => updateColor("foreground", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Accent"
                  value={currentColors.accent}
                  onChange={(v) => updateColor("accent", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Muted"
                  value={currentColors.muted}
                  onChange={(v) => updateColor("muted", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Success"
                  value={currentColors.success || "142 71% 45%"}
                  onChange={(v) => updateColor("success", v)}
                  disabled={disabled}
                />
                <ColorInput
                  label="Warning"
                  value={currentColors.warning || "38 92% 50%"}
                  onChange={(v) => updateColor("warning", v)}
                  disabled={disabled}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="typography" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Typography Settings</CardTitle>
              <CardDescription className="text-xs">
                Configure fonts and text styles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Font Family</Label>
                <Select defaultValue="inter" disabled={disabled}>
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map((font) => (
                      <SelectItem key={font.value} value={font.value}>
                        {font.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Base Font Size</Label>
                <Select defaultValue="16" disabled={disabled}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="14">14px</SelectItem>
                    <SelectItem value="16">16px</SelectItem>
                    <SelectItem value="18">18px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="layout" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Layout Settings</CardTitle>
              <CardDescription className="text-xs">
                Configure spacing and border styles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Border Radius</Label>
                  <span className="text-sm text-muted-foreground">
                    {parseRadius(radius)}px
                  </span>
                </div>
                <Slider
                  value={[parseRadius(radius)]}
                  onValueChange={(value) =>
                    onSetRadius(formatRadius(value[0]))
                  }
                  min={0}
                  max={20}
                  step={2}
                  className="w-full"
                  disabled={disabled}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Compact Mode</Label>
                  <p className="text-xs text-muted-foreground">
                    Reduce spacing for denser layouts
                  </p>
                </div>
                <Switch disabled={disabled} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Show Borders</Label>
                  <p className="text-xs text-muted-foreground">
                    Display borders on cards and sections
                  </p>
                </div>
                <Switch checked disabled={disabled} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {scopeLabel} Branding
              </CardTitle>
              <CardDescription className="text-xs">
                {scopeLabel === "Platform"
                  ? "Upload custom logos and branding assets for the platform"
                  : "Upload your organisation's logo and favicon"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Portal Name */}
              <div className="space-y-2">
                <Label htmlFor={`portal-name-${scopeLabel}`}>
                  Portal Name
                </Label>
                <Input
                  id={`portal-name-${scopeLabel}`}
                  placeholder="e.g., Partner Portal, Health Portal"
                  value={portalName}
                  onChange={(e) => onSetPortalName(e.target.value)}
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">
                  Displayed in the sidebar below the organisation name. Leave
                  empty{" "}
                  {scopeLabel === "Platform"
                    ? 'for default "Portal".'
                    : "to use the platform default."}
                </p>
              </div>

              {/* Logo Upload — two variants: light background and dark background */}
              <div className="space-y-2">
                <Label>{scopeLabel} Logo</Label>
                <p className="text-xs text-muted-foreground">
                  Upload two versions of your logo for different backgrounds.
                  The template system automatically selects the best version
                  based on partner brand colors.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {(["light_bg", "dark_bg"] as const).map((variant) => {
                    const variantUrl = logoVariantUrls?.[variant];
                    // Fall back to the legacy default logo for light_bg display
                    const displayUrl =
                      variant === "light_bg" && !variantUrl
                        ? logoUrl
                        : variantUrl;
                    const isUploading = uploadingLogoVariant === variant;
                    const label =
                      variant === "light_bg"
                        ? "Light Backgrounds"
                        : "Dark Backgrounds";
                    const desc =
                      variant === "light_bg"
                        ? "Your standard logo for light/white backgrounds"
                        : "A lighter logo version for dark/colored backgrounds";

                    const handleDragOver = (e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                    };

                    const handleDrop = (e: React.DragEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (disabled || isUploading || readOnly || !onLogoVariantUpload) return;
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.type.startsWith("image/")) {
                        onLogoVariantUpload(variant, file);
                      }
                    };

                    return (
                      <div
                        key={variant}
                        className="rounded-lg border p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{label}</p>
                          {!readOnly && displayUrl && onLogoVariantDelete && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                              onClick={() => onLogoVariantDelete(variant)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>

                        {!readOnly && onLogoVariantUpload ? (
                          <label
                            className={`block cursor-pointer ${disabled || isUploading ? "pointer-events-none opacity-50" : ""}`}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          >
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/svg+xml,image/webp"
                              className="hidden"
                              disabled={disabled || isUploading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) onLogoVariantUpload(variant, file);
                                e.target.value = "";
                              }}
                            />
                            {displayUrl ? (
                              <div
                                className={`h-20 w-full rounded border-2 border-dashed border-transparent hover:border-primary/50 overflow-hidden flex items-center justify-center transition-colors ${
                                  variant === "dark_bg"
                                    ? "bg-gray-800"
                                    : "bg-white"
                                }`}
                              >
                                {isUploading ? (
                                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                ) : (
                                  <img
                                    src={displayUrl}
                                    alt={label}
                                    className="h-full max-w-full object-contain"
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="flex h-20 w-full flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50 transition-colors">
                                {isUploading ? (
                                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                ) : (
                                  <>
                                    <Image className="h-5 w-5 text-muted-foreground/50" />
                                    <span className="text-[10px] text-muted-foreground">
                                      Click or drag to upload
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </label>
                        ) : (
                          displayUrl ? (
                            <div
                              className={`h-20 w-full rounded border overflow-hidden flex items-center justify-center ${
                                variant === "dark_bg"
                                  ? "bg-gray-800"
                                  : "bg-white"
                              }`}
                            >
                              <img
                                src={displayUrl}
                                alt={label}
                                className="h-full max-w-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="flex h-20 w-full items-center justify-center rounded border-2 border-dashed border-muted-foreground/25">
                              <Image className="h-5 w-5 text-muted-foreground/50" />
                            </div>
                          )
                        )}

                        <p className="text-[10px] text-muted-foreground">
                          {desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Favicon Upload */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Favicon</Label>
                  {!readOnly && faviconUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      onClick={onFaviconDelete}
                      disabled={disabled}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {!readOnly ? (
                  <label
                    className={`block cursor-pointer ${disabled || uploadingFavicon ? "pointer-events-none opacity-50" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (disabled || uploadingFavicon) return;
                      const file = e.dataTransfer.files?.[0];
                      if (file && (file.type.startsWith("image/") || file.name.endsWith(".ico"))) {
                        onFaviconUpload(file);
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept="image/png,image/x-icon,image/vnd.microsoft.icon"
                      className="hidden"
                      disabled={disabled || uploadingFavicon}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onFaviconUpload(file);
                        e.target.value = "";
                      }}
                    />
                    {faviconUrl ? (
                      <div className="flex items-center gap-4 p-4 border-2 border-dashed border-transparent hover:border-primary/50 rounded-lg transition-colors">
                        {uploadingFavicon ? (
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        ) : (
                          <img
                            src={faviconUrl}
                            alt="Favicon"
                            className="h-8 w-8 object-contain"
                          />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium">Current favicon</p>
                          <p className="text-xs text-muted-foreground">Click or drag to replace</p>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full border-2 border-dashed border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50 rounded-lg p-6 text-center transition-colors">
                        {uploadingFavicon ? (
                          <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin text-muted-foreground" />
                        ) : (
                          <Image className="h-6 w-6 mx-auto mb-2 text-muted-foreground/50" />
                        )}
                        <p className="text-sm text-muted-foreground">
                          {uploadingFavicon ? "Uploading..." : "Click or drag to upload"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          ICO or PNG (32x32px recommended)
                        </p>
                      </div>
                    )}
                  </label>
                ) : faviconUrl ? (
                  <div className="flex items-center gap-4 p-4 border rounded-lg">
                    <img
                      src={faviconUrl}
                      alt="Favicon"
                      className="h-8 w-8 object-contain"
                    />
                    <p className="text-sm font-medium">Current favicon</p>
                  </div>
                ) : (
                  <div className="w-full border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                    <Image className="h-6 w-6 mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">No favicon uploaded</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Custom hook: useOrgTheme -- manages all state & handlers for org-scoped theme
// ---------------------------------------------------------------------------

function useOrgTheme(token: string | null, organisationId: string | undefined) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasCustomTheme, setHasCustomTheme] = useState(false);
  const [mode, setMode] = useState<"light" | "dark" | "system">("system");
  const [lightColors, setLightColors] =
    useState<ThemeColorMode>(defaultLightColors);
  const [darkColors, setDarkColors] =
    useState<ThemeColorMode>(defaultDarkColors);
  const [radius, setRadius] = useState("0.5rem");
  const [portalName, setPortalName] = useState("");
  const [logoS3Key, setLogoS3Key] = useState<string | null>(null);
  const [faviconS3Key, setFaviconS3Key] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [logoVariantUrls, setLogoVariantUrls] = useState<{
    light_bg?: string;
    dark_bg?: string;
  }>({});
  const [uploadingLogoVariant, setUploadingLogoVariant] = useState<string | null>(null);

  const [originalState, setOriginalState] = useState<{
    lightColors: ThemeColorMode;
    darkColors: ThemeColorMode;
    radius: string;
    portalName: string;
    mode: "light" | "dark" | "system";
  } | null>(null);

  useEffect(() => {
    if (token && organisationId) {
      loadTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, organisationId]);

  const loadTheme = async () => {
    if (!token || !organisationId) return;
    setLoading(true);
    try {
      const response = await themeApi.getTheme(token, organisationId);
      setHasCustomTheme(response.has_custom_theme);

      if (response.theme_config) {
        setLightColors(
          withDefaultColors(response.theme_config.light, defaultLightColors),
        );
        setDarkColors(
          withDefaultColors(response.theme_config.dark, defaultDarkColors),
        );
        setRadius(response.theme_config.radius);
        setPortalName(response.theme_config.branding?.portal_name || "");

        setOriginalState({
          lightColors: response.theme_config.light,
          darkColors: response.theme_config.dark,
          radius: response.theme_config.radius,
          portalName: response.theme_config.branding?.portal_name || "",
          mode: "system",
        });

        if (response.theme_config.branding?.logo_s3_key) {
          setLogoS3Key(response.theme_config.branding.logo_s3_key);
          try {
            const logoRes = await themeApi.getAssetUrl(
              token,
              organisationId,
              "logo",
            );
            setLogoUrl(logoRes.url);
          } catch {
            // Ignore if asset doesn't exist
          }
        }
        // Fetch logo variant URLs
        const logoVariants = response.theme_config.branding?.logo_variants;
        if (logoVariants) {
          const newVariantUrls: { light_bg?: string; dark_bg?: string } = {};
          for (const variant of ["light_bg", "dark_bg"] as const) {
            if (logoVariants[variant]) {
              try {
                const vRes = await themeApi.getAssetUrl(
                  token,
                  organisationId,
                  "logo",
                  variant,
                );
                newVariantUrls[variant] = vRes.url;
              } catch {
                // Ignore
              }
            }
          }
          setLogoVariantUrls(newVariantUrls);
        } else {
          setLogoVariantUrls({});
        }

        if (response.theme_config.branding?.favicon_s3_key) {
          setFaviconS3Key(response.theme_config.branding.favicon_s3_key);
          try {
            const favRes = await themeApi.getAssetUrl(
              token,
              organisationId,
              "favicon",
            );
            setFaviconUrl(favRes.url);
          } catch {
            // Ignore if asset doesn't exist
          }
        }
      }
    } catch (error) {
      console.error("Failed to load organisation theme:", error);
      toast.error("Failed to load theme settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token || !organisationId) return;
    const colors = prepareColorsForSave(lightColors, darkColors);
    if (!colors) return;
    setLightColors(colors.light);
    setDarkColors(colors.dark);
    setSaving(true);
    try {
      const themeConfig: ThemeConfig = {
        version: "1.0",
        format: "hsl",
        light: colors.light,
        dark: colors.dark,
        radius,
        branding: {
          logo_s3_key: logoS3Key || undefined,
          favicon_s3_key: faviconS3Key || undefined,
          portal_name: portalName || undefined,
        },
      };
      await themeApi.updateTheme(token, organisationId, themeConfig);
      setHasCustomTheme(true);
      setIsEditing(false);
      setOriginalState({
        lightColors: colors.light,
        darkColors: colors.dark,
        radius,
        portalName,
        mode,
      });
      toast.success("Theme saved successfully");
    } catch (error) {
      console.error("Failed to save theme:", error);
      toast.error("Failed to save theme");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!token || !organisationId) return;
    setSaving(true);
    try {
      await themeApi.resetTheme(token, organisationId);
      setLightColors(defaultLightColors);
      setDarkColors(defaultDarkColors);
      setRadius("0.5rem");
      setLogoS3Key(null);
      setFaviconS3Key(null);
      setLogoUrl(null);
      setFaviconUrl(null);
      setLogoVariantUrls({});
      setPortalName("");
      setHasCustomTheme(false);
      setIsEditing(false);
      toast.success("Theme reset to defaults");
    } catch (error) {
      console.error("Failed to reset theme:", error);
      toast.error("Failed to reset theme");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalState) {
      setLightColors(originalState.lightColors);
      setDarkColors(originalState.darkColors);
      setRadius(originalState.radius);
      setPortalName(originalState.portalName);
      setMode(originalState.mode);
    }
    setIsEditing(false);
  };

  const handleLogoVariantUpload = async (
    variant: "light_bg" | "dark_bg",
    file: File,
  ) => {
    if (!token || !organisationId) return;
    setUploadingLogoVariant(variant);
    try {
      await themeApi.uploadAsset(token, organisationId, "logo", file, variant);
      const urlRes = await themeApi.getAssetUrl(
        token,
        organisationId,
        "logo",
        variant,
      );
      setLogoVariantUrls((prev) => ({ ...prev, [variant]: urlRes.url }));
      // light_bg also becomes the default logo (sidebar, etc.)
      if (variant === "light_bg") {
        setLogoUrl(urlRes.url);
      }
      toast.success(
        `${variant === "light_bg" ? "Light" : "Dark"} background logo uploaded`,
      );
    } catch {
      toast.error("Failed to upload logo variant");
    } finally {
      setUploadingLogoVariant(null);
    }
  };

  const handleLogoVariantDelete = async (variant: "light_bg" | "dark_bg") => {
    if (!token || !organisationId) return;
    try {
      await themeApi.deleteAsset(token, organisationId, "logo", variant);
      setLogoVariantUrls((prev) => {
        const updated = { ...prev };
        delete updated[variant];
        return updated;
      });
      // light_bg is also the default logo
      if (variant === "light_bg") {
        setLogoUrl(null);
      }
      toast.success(
        `${variant === "light_bg" ? "Light" : "Dark"} background logo deleted`,
      );
    } catch {
      toast.error("Failed to delete logo variant");
    }
  };

  const handleFaviconUpload = async (file: File) => {
    if (!token || !organisationId) return;
    setUploadingFavicon(true);
    try {
      const response = await themeApi.uploadAsset(
        token,
        organisationId,
        "favicon",
        file,
      );
      setFaviconS3Key(response.s3_key);
      const urlRes = await themeApi.getAssetUrl(
        token,
        organisationId,
        "favicon",
      );
      setFaviconUrl(urlRes.url);
      toast.success("Favicon uploaded successfully");
    } catch (error) {
      console.error("Failed to upload favicon:", error);
      toast.error("Failed to upload favicon");
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleFaviconDelete = async () => {
    if (!token || !organisationId) return;
    try {
      await themeApi.deleteAsset(token, organisationId, "favicon");
      setFaviconS3Key(null);
      setFaviconUrl(null);
      toast.success("Favicon deleted");
    } catch (error) {
      console.error("Failed to delete favicon:", error);
      toast.error("Failed to delete favicon");
    }
  };

  const applyPreset = (preset: (typeof COLOR_PRESETS)[0]) => {
    setLightColors((prev) => ({
      ...prev,
      primary: preset.primary,
      accent: preset.accent,
    }));
    setDarkColors((prev) => ({
      ...prev,
      primary: preset.primary,
      accent: preset.accent,
    }));
  };

  return {
    loading,
    saving,
    isEditing,
    setIsEditing,
    hasCustomTheme,
    mode,
    setMode,
    lightColors,
    darkColors,
    radius,
    setRadius,
    portalName,
    setPortalName,
    logoUrl,
    faviconUrl,
    uploadingFavicon,
    updateLightColor: (key: keyof ThemeColorMode, value: string) =>
      setLightColors((prev) => ({ ...prev, [key]: value })),
    updateDarkColor: (key: keyof ThemeColorMode, value: string) =>
      setDarkColors((prev) => ({ ...prev, [key]: value })),
    handleSave,
    handleReset,
    handleCancel,
    handleFaviconUpload,
    handleFaviconDelete,
    logoVariantUrls,
    uploadingLogoVariant,
    handleLogoVariantUpload,
    handleLogoVariantDelete,
    applyPreset,
  };
}

// ---------------------------------------------------------------------------
// Custom hook: usePlatformTheme -- manages all state & handlers for platform
// ---------------------------------------------------------------------------

function usePlatformTheme(token: string | null) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [mode, setMode] = useState<"light" | "dark" | "system">("system");
  const [lightColors, setLightColors] =
    useState<ThemeColorMode>(defaultLightColors);
  const [darkColors, setDarkColors] =
    useState<ThemeColorMode>(defaultDarkColors);
  const [radius, setRadius] = useState("0.5rem");
  const [portalName, setPortalName] = useState("");
  const [hasCustomTheme, setHasCustomTheme] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const [originalState, setOriginalState] = useState<{
    lightColors: ThemeColorMode;
    darkColors: ThemeColorMode;
    radius: string;
    portalName: string;
    mode: "light" | "dark" | "system";
    logoS3Key?: string;
    faviconS3Key?: string;
  } | null>(null);

  // Track S3 keys internally for branding config rebuilds
  const [logoS3Key, setLogoS3Key] = useState<string | undefined>(undefined);
  const [faviconS3Key, setFaviconS3Key] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    if (token) {
      loadTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadTheme = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await themeApi.getPlatformTheme(token);
      if (response.theme_config) {
        setLightColors(
          withDefaultColors(response.theme_config.light, defaultLightColors),
        );
        setDarkColors(
          withDefaultColors(response.theme_config.dark, defaultDarkColors),
        );
        setRadius(response.theme_config.radius);
        setPortalName(response.theme_config.branding?.portal_name || "");
        setLogoS3Key(response.theme_config.branding?.logo_s3_key);
        setFaviconS3Key(response.theme_config.branding?.favicon_s3_key);
        setHasCustomTheme(true);

        setOriginalState({
          lightColors: response.theme_config.light,
          darkColors: response.theme_config.dark,
          radius: response.theme_config.radius,
          portalName: response.theme_config.branding?.portal_name || "",
          mode: "system",
          logoS3Key: response.theme_config.branding?.logo_s3_key,
          faviconS3Key: response.theme_config.branding?.favicon_s3_key,
        });

        if (response.theme_config.branding?.logo_s3_key) {
          try {
            const logoRes = await themeApi.getPlatformAssetUrl(token, "logo");
            setLogoUrl(logoRes.url);
          } catch {
            setLogoUrl(null);
          }
        }
        if (response.theme_config.branding?.favicon_s3_key) {
          try {
            const favRes = await themeApi.getPlatformAssetUrl(
              token,
              "favicon",
            );
            setFaviconUrl(favRes.url);
          } catch {
            setFaviconUrl(null);
          }
        }
      } else {
        setHasCustomTheme(false);
        setOriginalState(null);
      }
    } catch (error) {
      console.error("Failed to load platform theme:", error);
      toast.error("Failed to load platform theme");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!token) return;
    const colors = prepareColorsForSave(lightColors, darkColors);
    if (!colors) return;
    setLightColors(colors.light);
    setDarkColors(colors.dark);
    setSaving(true);
    try {
      const themeConfig: ThemeConfig = {
        version: "1.0",
        format: "hsl",
        light: colors.light,
        dark: colors.dark,
        radius,
        branding: {
          logo_s3_key: logoS3Key,
          favicon_s3_key: faviconS3Key,
          portal_name: portalName || undefined,
        },
      };
      const response = await themeApi.updatePlatformTheme(token, themeConfig);
      if (response.theme_config) {
        setLightColors(
          withDefaultColors(response.theme_config.light, defaultLightColors),
        );
        setDarkColors(
          withDefaultColors(response.theme_config.dark, defaultDarkColors),
        );
        setRadius(response.theme_config.radius);
        setPortalName(response.theme_config.branding?.portal_name || "");
      }
      setHasCustomTheme(true);
      setIsEditing(false);
      setOriginalState({
        lightColors: colors.light,
        darkColors: colors.dark,
        radius,
        portalName,
        mode,
        logoS3Key,
        faviconS3Key,
      });
      toast.success("Platform theme saved successfully");
    } catch (error) {
      console.error("Failed to save platform theme:", error);
      toast.error("Failed to save platform theme");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!token) return;
    setSaving(true);
    try {
      await themeApi.resetPlatformTheme(token);
      setLightColors(defaultLightColors);
      setDarkColors(defaultDarkColors);
      setRadius("0.5rem");
      setPortalName("");
      setLogoS3Key(undefined);
      setFaviconS3Key(undefined);
      setLogoUrl(null);
      setFaviconUrl(null);
      setHasCustomTheme(false);
      setIsEditing(false);
      toast.success("Platform theme reset to defaults");
    } catch (error) {
      console.error("Failed to reset platform theme:", error);
      toast.error("Failed to reset platform theme");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalState) {
      setLightColors(originalState.lightColors);
      setDarkColors(originalState.darkColors);
      setRadius(originalState.radius);
      setPortalName(originalState.portalName);
      setMode(originalState.mode);
      setLogoS3Key(originalState.logoS3Key);
      setFaviconS3Key(originalState.faviconS3Key);
    }
    setIsEditing(false);
  };

  const handleFaviconUpload = async (file: File) => {
    if (!token) return;
    setUploadingFavicon(true);
    try {
      const response = await themeApi.uploadPlatformAsset(
        token,
        "favicon",
        file,
      );
      setFaviconS3Key(response.s3_key);
      const urlRes = await themeApi.getPlatformAssetUrl(token, "favicon");
      setFaviconUrl(urlRes.url);
      toast.success("Favicon uploaded successfully");
    } catch (error) {
      console.error("Failed to upload favicon:", error);
      toast.error("Failed to upload favicon");
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleFaviconDelete = async () => {
    if (!token) return;
    try {
      await themeApi.deletePlatformAsset(token, "favicon");
      setFaviconS3Key(undefined);
      setFaviconUrl(null);
      toast.success("Favicon deleted");
    } catch (error) {
      console.error("Failed to delete favicon:", error);
      toast.error("Failed to delete favicon");
    }
  };

  const applyPreset = (preset: (typeof COLOR_PRESETS)[0]) => {
    setLightColors((prev) => ({
      ...prev,
      primary: preset.primary,
      accent: preset.accent,
    }));
    setDarkColors((prev) => ({
      ...prev,
      primary: preset.primary,
      accent: preset.accent,
    }));
  };

  return {
    loading,
    saving,
    isEditing,
    setIsEditing,
    hasCustomTheme,
    mode,
    setMode,
    lightColors,
    darkColors,
    radius,
    setRadius,
    portalName,
    setPortalName,
    logoUrl,
    faviconUrl,
    uploadingFavicon,
    updateLightColor: (key: keyof ThemeColorMode, value: string) =>
      setLightColors((prev) => ({ ...prev, [key]: value })),
    updateDarkColor: (key: keyof ThemeColorMode, value: string) =>
      setDarkColors((prev) => ({ ...prev, [key]: value })),
    handleSave,
    handleReset,
    handleCancel,
    handleFaviconUpload,
    handleFaviconDelete,
    applyPreset,
  };
}

// ---------------------------------------------------------------------------
// Org Brand Profile hook
// ---------------------------------------------------------------------------

function useOrgBrandProfile(token: string | null, organisationId: string | undefined) {
  const [profile, setProfile] = useState<OrgBrandProfile>({});
  const [originalProfile, setOriginalProfile] = useState<OrgBrandProfile>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!token || !organisationId) return;
    setLoading(true);
    brandApi
      .getOrgBrandProfile(token, organisationId)
      .then((res) => {
        const bp = res.brand_profile ?? {};
        setProfile(bp);
        setOriginalProfile(bp);
      })
      .catch(() => {
        /* no brand profile yet */
      })
      .finally(() => setLoading(false));
  }, [token, organisationId]);

  const handleSave = async (overrideProfile?: OrgBrandProfile) => {
    if (!token || !organisationId) return;
    const payload = overrideProfile ?? profile;
    setSaving(true);
    try {
      const res = await brandApi.updateOrgBrandProfile(token, organisationId, payload);
      const bp = res.brand_profile ?? {};
      setProfile(bp);
      setOriginalProfile(bp);
      setIsEditing(false);
      toast.success("Brand identity saved");
    } catch {
      toast.error("Failed to save brand identity");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setProfile(originalProfile);
    setIsEditing(false);
  };

  return { profile, setProfile, loading, saving, isEditing, setIsEditing, handleSave, handleCancel };
}

// ---------------------------------------------------------------------------
// Brand Identity Panel (owners and admins edit; members read)
// ---------------------------------------------------------------------------

const BRAND_ARRAY_KEYS = ["tone_attributes", "key_differentiators", "key_messages", "industry_expertise", "avoid_terms"] as const;

function BrandIdentityPanel({
  token,
  organisationId,
  readOnly,
}: {
  token: string | null;
  organisationId: string | undefined;
  /** Members may read the brand profile but not save it (cp rbac_policy.csv). */
  readOnly: boolean;
}) {
  const bp = useOrgBrandProfile(token, organisationId);

  // Array fields stored as raw strings while editing to allow typing commas
  const [arrayDrafts, setArrayDrafts] = useState<Record<string, string>>({});

  // Sync drafts when profile loads or editing starts
  useEffect(() => {
    const drafts: Record<string, string> = {};
    for (const key of BRAND_ARRAY_KEYS) {
      const val = bp.profile[key];
      drafts[key] = Array.isArray(val) ? val.join(", ") : "";
    }
    setArrayDrafts(drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally listing only the 5 array fields, not the full bp.profile object
  }, [bp.profile.tone_attributes, bp.profile.key_differentiators, bp.profile.key_messages, bp.profile.industry_expertise, bp.profile.avoid_terms]);

  const updateField = (key: keyof OrgBrandProfile, value: string) => {
    bp.setProfile((prev) => ({ ...prev, [key]: value }));
  };

  const updateArrayDraft = (key: string, value: string) => {
    setArrayDrafts((prev) => ({ ...prev, [key]: value }));
  };

  // Build final profile with arrays resolved and save directly
  const handleSave = () => {
    const finalProfile = { ...bp.profile };
    for (const key of BRAND_ARRAY_KEYS) {
      (finalProfile as Record<string, unknown>)[key] = (arrayDrafts[key] ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    bp.handleSave(finalProfile);
  };

  const getArrayDraft = (key: string) => arrayDrafts[key] ?? "";

  if (bp.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Brand Identity</h2>
          <p className="text-sm text-muted-foreground">
            Define your organisation's brand voice and messaging for AI content generation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && bp.isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={bp.handleCancel} disabled={bp.saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={bp.saving}>
                {bp.saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </>
          )}
          {!readOnly && !bp.isEditing && (
            <Button variant="outline" size="sm" onClick={() => bp.setIsEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Company & Voice</CardTitle>
          <CardDescription>
            Core identity used across all AI-generated content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={bp.profile.company_name ?? ""}
                onChange={(e) => updateField("company_name", e.target.value)}
                disabled={!bp.isEditing}
                placeholder="e.g. Acme Corp"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand_name">Brand Name</Label>
              <Input
                id="brand_name"
                value={bp.profile.brand_name ?? ""}
                onChange={(e) => updateField("brand_name", e.target.value)}
                disabled={!bp.isEditing}
                placeholder="If different from company name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tagline">Tagline</Label>
            <Input
              id="tagline"
              value={bp.profile.tagline ?? ""}
              onChange={(e) => updateField("tagline", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. Empowering businesses through innovation"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand_voice">Brand Voice</Label>
            <Input
              id="brand_voice"
              value={bp.profile.brand_voice ?? ""}
              onChange={(e) => updateField("brand_voice", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. Professional yet approachable"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tone_attributes">Tone Attributes</Label>
            <Input
              id="tone_attributes"
              value={getArrayDraft("tone_attributes")}
              onChange={(e) => updateArrayDraft("tone_attributes", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. confident, warm, expert (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">Comma-separated list</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Messaging & Content</CardTitle>
          <CardDescription>
            Key messages and content guidelines for generated material.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="value_proposition">Value Proposition</Label>
            <Textarea
              id="value_proposition"
              value={bp.profile.value_proposition ?? ""}
              onChange={(e) => updateField("value_proposition", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="What unique value does your company deliver?"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="key_differentiators">Key Differentiators</Label>
            <Input
              id="key_differentiators"
              value={getArrayDraft("key_differentiators")}
              onChange={(e) => updateArrayDraft("key_differentiators", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. 24/7 support, AI-powered, industry-leading (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">Comma-separated list</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="key_messages">Key Messages</Label>
            <Input
              id="key_messages"
              value={getArrayDraft("key_messages")}
              onChange={(e) => updateArrayDraft("key_messages", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. Transform your workflow, Trusted by 500+ companies (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">Comma-separated list</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="boilerplate">
              Boilerplate
              <span className="ml-2 text-xs text-muted-foreground">
                {(bp.profile.boilerplate ?? "").length}/500
              </span>
            </Label>
            <Textarea
              id="boilerplate"
              value={bp.profile.boilerplate ?? ""}
              onChange={(e) => {
                if (e.target.value.length <= 500) updateField("boilerplate", e.target.value);
              }}
              disabled={!bp.isEditing}
              placeholder="Standard company description for content footers"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="industry_expertise">Industry Expertise</Label>
            <Input
              id="industry_expertise"
              value={getArrayDraft("industry_expertise")}
              onChange={(e) => updateArrayDraft("industry_expertise", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. SaaS, FinTech, Healthcare (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">Comma-separated list</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="avoid_terms">Terms to Avoid</Label>
            <Input
              id="avoid_terms"
              value={getArrayDraft("avoid_terms")}
              onChange={(e) => updateArrayDraft("avoid_terms", e.target.value)}
              disabled={!bp.isEditing}
              placeholder="e.g. cheap, basic, simple (comma-separated)"
            />
            <p className="text-xs text-muted-foreground">Words or phrases AI should never use in generated content</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Social Links</CardTitle>
          <CardDescription>
            Default URLs for partner social-media icons when a partner has none
            of their own. Each URL falls back independently — a partner can
            override X while inheriting LinkedIn from the org. Contact fields
            (email, phone, website, address, boilerplate) are intentionally
            not part of this fallback — vendor-self partners render those
            blank by design.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="social_facebook">Facebook</Label>
              <Input
                id="social_facebook"
                value={bp.profile.social_links?.facebook ?? ""}
                onChange={(e) =>
                  bp.setProfile((prev) => ({
                    ...prev,
                    social_links: { ...(prev.social_links ?? {}), facebook: e.target.value },
                  }))
                }
                disabled={!bp.isEditing}
                placeholder="https://facebook.com/yourcompany"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_youtube">YouTube</Label>
              <Input
                id="social_youtube"
                value={bp.profile.social_links?.youtube ?? ""}
                onChange={(e) =>
                  bp.setProfile((prev) => ({
                    ...prev,
                    social_links: { ...(prev.social_links ?? {}), youtube: e.target.value },
                  }))
                }
                disabled={!bp.isEditing}
                placeholder="https://youtube.com/@yourcompany"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_twitter">X / Twitter</Label>
              <Input
                id="social_twitter"
                value={bp.profile.social_links?.twitter ?? ""}
                onChange={(e) =>
                  bp.setProfile((prev) => ({
                    ...prev,
                    social_links: { ...(prev.social_links ?? {}), twitter: e.target.value },
                  }))
                }
                disabled={!bp.isEditing}
                placeholder="https://x.com/yourcompany"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="social_linkedin">LinkedIn</Label>
              <Input
                id="social_linkedin"
                value={bp.profile.social_links?.linkedin ?? ""}
                onChange={(e) =>
                  bp.setProfile((prev) => ({
                    ...prev,
                    social_links: { ...(prev.social_links ?? {}), linkedin: e.target.value },
                  }))
                }
                disabled={!bp.isEditing}
                placeholder="https://linkedin.com/company/yourcompany"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function ThemeEditorPage() {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const isUserSuperAdmin = isSuperAdmin(session);
  const canEditTheme = can("edit", "theme");
  const organisationId = selectedOrganisation?.organisationId;

  const org = useOrgTheme(token, organisationId);
  const platform = usePlatformTheme(isUserSuperAdmin ? token : null);

  const orgPanel = (
    <ThemeEditorPanel
      loading={org.loading}
      saving={org.saving}
      isEditing={org.isEditing}
      setIsEditing={org.setIsEditing}
      themeMode={org.mode}
      setThemeMode={org.setMode}
      lightColors={org.lightColors}
      darkColors={org.darkColors}
      radius={org.radius}
      portalName={org.portalName}
      logoUrl={org.logoUrl}
      faviconUrl={org.faviconUrl}
      uploadingFavicon={org.uploadingFavicon}
      hasCustomTheme={org.hasCustomTheme}
      readOnly={!canEditTheme}
      onUpdateLightColor={org.updateLightColor}
      onUpdateDarkColor={org.updateDarkColor}
      onSetRadius={org.setRadius}
      onSetPortalName={org.setPortalName}
      onApplyPreset={org.applyPreset}
      onSave={org.handleSave}
      onReset={org.handleReset}
      onCancel={org.handleCancel}
      onFaviconUpload={org.handleFaviconUpload}
      onFaviconDelete={org.handleFaviconDelete}
      logoVariantUrls={org.logoVariantUrls}
      uploadingLogoVariant={org.uploadingLogoVariant}
      onLogoVariantUpload={org.handleLogoVariantUpload}
      onLogoVariantDelete={org.handleLogoVariantDelete}
      scopeLabel="Organisation"
    />
  );

  if (isUserSuperAdmin) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Theme Editor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage organisation and platform themes
          </p>
        </div>

        <Tabs defaultValue="org-theme">
          <TabsList>
            <TabsTrigger value="org-theme">Organisation Theme</TabsTrigger>
            <TabsTrigger value="brand-identity">
              Brand Identity
            </TabsTrigger>
            <TabsTrigger value="platform-defaults">
              Platform Defaults
            </TabsTrigger>
          </TabsList>

          <TabsContent value="org-theme" className="mt-6">
            {orgPanel}
          </TabsContent>

          <TabsContent value="brand-identity" className="mt-6">
            <BrandIdentityPanel token={token} organisationId={organisationId} readOnly={!canEditTheme} />
          </TabsContent>

          <TabsContent value="platform-defaults" className="mt-6">
            <ThemeEditorPanel
              loading={platform.loading}
              saving={platform.saving}
              isEditing={platform.isEditing}
              setIsEditing={platform.setIsEditing}
              themeMode={platform.mode}
              setThemeMode={platform.setMode}
              lightColors={platform.lightColors}
              darkColors={platform.darkColors}
              radius={platform.radius}
              portalName={platform.portalName}
              logoUrl={platform.logoUrl}
              faviconUrl={platform.faviconUrl}
              uploadingFavicon={platform.uploadingFavicon}
              hasCustomTheme={platform.hasCustomTheme}
              readOnly={false}
              onUpdateLightColor={platform.updateLightColor}
              onUpdateDarkColor={platform.updateDarkColor}
              onSetRadius={platform.setRadius}
              onSetPortalName={platform.setPortalName}
              onApplyPreset={platform.applyPreset}
              onSave={platform.handleSave}
              onReset={platform.handleReset}
              onCancel={platform.handleCancel}
              onFaviconUpload={platform.handleFaviconUpload}
              onFaviconDelete={platform.handleFaviconDelete}
              scopeLabel="Platform"
            />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Non-superadmin: org theme + brand identity (the org owns its brand
  // profile, including Social Links used by partner-fallback rendering).
  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="h-6 w-6" />
            Theme Editor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customise your organisation's theme and brand identity
          </p>
        </div>
        <ReadOnlyBadge show={!canEditTheme} />
      </div>

      <Tabs defaultValue="org-theme">
        <TabsList>
          <TabsTrigger value="org-theme">Theme</TabsTrigger>
          <TabsTrigger value="brand-identity">Brand Identity</TabsTrigger>
        </TabsList>

        <TabsContent value="org-theme" className="mt-6">
          {orgPanel}
        </TabsContent>

        <TabsContent value="brand-identity" className="mt-6">
          <BrandIdentityPanel token={token} organisationId={organisationId} readOnly={!canEditTheme} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
